import { useCallback, useEffect, useMemo } from 'react';

import { useAuthContext } from '@fhs/auth/src/hooks';
import { useGiftCardContext } from '@fhs/payments';
import {
  CartPaymentCardType,
  ICommitDeliveryInput,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useConfigValue } from '@fhs-legacy/frontend/src/hooks/configs/use-config-value';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { ServiceMode, useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { ISOs, getCountryAndCurrencyCodes } from '@fhs-legacy/frontend/src/utils/form/constants';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { isCreditCardExpired } from '@fhs-legacy/frontend/src/utils/payment';
import type { ICartEntry, IServerOrder } from '@rbi-ctg/menu';

import {
  BasePipelineContext,
  GiftCardContext,
  PipelineStepContext,
  usePaymentMethodStore,
} from '..';
import { usePaymentFlowStore } from '../../state/payment-stores/payment-flow-store';
import { useTipStore } from '../../state/tip-store';
import type { CommittedOrder } from '../payment-types';
import { EncryptionService } from '../services/encryption-service';

interface GiftCardAccountData {
  accountIdentifier: string;
  cardNumber?: string;
  currentBalance?: number;
}

import useBuildPaymentProcessor from './use-build-payment-processor';

export function useBuildPipelineContext() {
  const paymentMethod = usePaymentMethodStore(s => s.selectedPaymentMethod);
  const newCardData = usePaymentMethodStore(s => s.newCardData);
  const savedCardData = usePaymentMethodStore(s => s.savedCardData);
  const creditType = usePaymentMethodStore(s => s.creditType);
  const selectedGiftCardAccountId = usePaymentMethodStore(s => s.selectedGiftCardAccountId);
  const setEncryptionService = usePaymentMethodStore(s => s.setEncryptionService);
  const userAccountsData = usePaymentMethodStore(s => s.userAccountsData);
  const { serverOrder } = useCartContext();
  const { user, updateUserInfo } = useAuthContext();
  const {
    orderPhoneNumber,
    deliveryAddress,
    deliveryInstructions,
    fireOrderIn: orderFireOrderIn,
    getCurrentFireOrderIn,
  } = useOrderContext();
  const { store } = useStoreContext();
  const { logPurchase } = useCRMEventsContext();
  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;
  const { currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const processor = useBuildPaymentProcessor();
  const appleConfig = useConfigValue({ key: 'apple', defaultValue: {}, isRegionalized: false });
  const googleConfig = useConfigValue({ key: 'google', defaultValue: {} });

  useEffect(() => {
    usePaymentFlowStore.getState().setPricedOrder(serverOrder as unknown as CommittedOrder | null);
  }, [serverOrder]);

  // Get real-time gift card balance from context (single source of truth)
  const { currentBalance: giftCardBalance, refetchGiftCardAccount } = useGiftCardContext();

  const userCognitoId = (user as { cognitoId?: string } | undefined)?.cognitoId;

  const tipCents = useTipStore(s => s.totalTipCentsForCommit);
  const delivery: ICommitDeliveryInput = useMemo(() => {
    return {
      instructions: deliveryInstructions,
      tipCents,
      pushNotification: true,
      dropoff: {
        ...deliveryAddress,
        phoneNumber: orderPhoneNumber,
      },
    };
  }, [deliveryAddress, deliveryInstructions, orderPhoneNumber, tipCents]);

  const maxCartLimit = useFlag('override-checkout-limit');
  const minDeliveryAmount = useFlag('override-checkout-delivery-minimum');

  const buildPipelineContext = useCallback((): PipelineStepContext | null => {
    const rbiOrderId = serverOrder?.rbiOrderId;
    const storeId = serverOrder?.cart?.storeDetails?.storeNumber;
    const serviceMode = serverOrder?.cart?.serviceMode;
    // typed as IBackendCartEntries
    const cartEntries = serverOrder?.cart?.cartEntries;
    const fireOrderIn = orderFireOrderIn ?? 0;
    const fullName = user?.details?.name;
    // NOTE: This is an unused field on the commitOrder call, setting to false so it doesn't break
    const skipCoolingPeriod = false;
    const cartSubTotal = serverOrder?.cart?.subTotalCents ?? 0;
    const cartTotal = serverOrder?.cart?.totalCents ?? 0;

    const serverOrderStatus = serverOrder?.status ?? '';
    const hasUnavailableCartEntries = (serverOrder?.cart?.unavailableCartEntries?.length ?? 0) > 0;

    // Early return if required fields are missing (order/cart always required)
    if (!rbiOrderId || !storeId || !serviceMode || !cartEntries) {
      return null;
    }

    const fullNameForContext = fullName ?? '';
    const isDigitalWallet = paymentMethod === 'applePay' || paymentMethod === 'googlePay';
    if (!fullNameForContext && !isDigitalWallet) {
      return null;
    }

    const baseContext: BasePipelineContext = {
      // type error for COMET and others
      processor: processor as any,
      creditType: creditType as CartPaymentCardType,
      rbiOrderId,
      storeId,
      serviceMode,
      cartEntries,

      fireOrderIn,
      getFireOrderIn: getCurrentFireOrderIn,
      delivery,
      fullName: fullNameForContext,
      userId: userCognitoId,
      skipCoolingPeriod,

      cartTotal,
      cartSubTotal,
      serverOrderStatus,
      unavailableCartEntries: hasUnavailableCartEntries,
      maxCartLimit,
      minDeliveryAmount,

      // Pass restaurant data from cached store context
      restaurantTimezone: store?.timezone ?? undefined,
      restaurantHours: store
        ? {
            deliveryHours: store.deliveryHours,
            curbsideHours: store.curbsideHours,
            driveThruHours: store.driveThruHours,
            diningRoomHours: store.diningRoomHours,
            pickupWindowHours: store.pickupWindowHours,
            cateringHours: null, // Not available in IStore type
          }
        : undefined,

      errors: [],

      appleConfig,
      googleConfig,

      /** Called when a new card is successfully vaulted (saved); use to set it as default payment method */
      onNewCardVaulted:
        user?.details && updateUserInfo
          ? async (accountIdentifier: string) => {
              await updateUserInfo(
                {
                  ...user.details,
                  defaultCheckoutPaymentMethodId: accountIdentifier,
                  defaultReloadPaymentMethodId: accountIdentifier,
                },
                { shouldMuteUserInfoErrors: true }
              );
            }
          : undefined,

      onCommitSuccess: async (remoteOrder: CommittedOrder) => {
        const quotedFeeCents = remoteOrder.delivery?.quotedFeeCents || 0;
        const deliveryFeeCents = remoteOrder.delivery?.feeCents || 0;
        const deliveryFeeDiscountCents = remoteOrder.delivery?.feeDiscountCents || 0;
        const deliveryGeographicalFeeCents = remoteOrder.delivery?.geographicalFeeCents || 0;
        const deliveryServiceFeeCents = remoteOrder.delivery?.serviceFeeCents || 0;
        const deliverySmallCartFeeCents = remoteOrder.delivery?.smallCartFeeCents || 0;
        const baseDeliveryFeeCents = remoteOrder.delivery?.baseDeliveryFeeCents || 0;
        const deliverySurchargeFeeCents = remoteOrder.delivery?.deliverySurchargeFeeCents || 0;

        const commitGiftCardBalance =
          remoteOrder.cart.payment?.cardType === 'GIFT_CARD'
            ? (await refetchGiftCardAccount())?.currentBalance
            : undefined;

        // logPurchase expects legacy IServerOrder / ICartEntry types; CommittedOrder is structurally compatible at runtime
        logPurchase(
          remoteOrder.cart.cartEntries as unknown as ICartEntry[],
          store,
          remoteOrder.cart.serviceMode as unknown as ServiceMode,
          remoteOrder as unknown as IServerOrder,
          {
            currencyCode,
            quotedFeeCents,
            baseDeliveryFeeCents,
            totalDeliveryOrderFeesCents: calculateTotalDeliveryOrderFeesCents(
              baseDeliveryFeeCents,
              deliverySurchargeFeeCents,
              deliveryServiceFeeCents,
              deliverySmallCartFeeCents,
              deliveryGeographicalFeeCents,
              deliveryFeeDiscountCents
            ),
            deliveryFeeCents: calculateDeliveryFeeCents(
              deliveryFeeCents,
              deliveryFeeDiscountCents,
              deliveryGeographicalFeeCents,
              deliveryServiceFeeCents,
              deliverySmallCartFeeCents
            ),
            deliverySurchargeFeeCents,
            deliveryFeeDiscountCents,
            deliveryGeographicalFeeCents,
            deliveryServiceFeeCents,
            deliverySmallCartFeeCents,
            fireOrderInMinutes: Math.round(fireOrderIn / 60),
            giftCardBalance: commitGiftCardBalance,
          }
        );
      },
    };

    switch (paymentMethod) {
      case 'savedCard': {
        // Saved card flow requires the account identifier
        if (!savedCardData) {
          return null;
        }

        if (isCreditCardExpired(savedCardData.expiryMonth, savedCardData.expiryYear)) {
          return null;
        }

        // Both Orbital and Worldpay use accountIdentifier
        const savedCardId = savedCardData.accountIdentifier;

        if (!savedCardId) {
          return null;
        }

        return {
          ...baseContext,
          paymentMethod: 'savedCard',
          savedCardId,
          last4: savedCardData.alias,
          expiryMonth: savedCardData.expiryMonth,
          expiryYear: savedCardData.expiryYear,
          // bin may not be available in savedCardData
        };
      }
      case 'applePay':
      case 'googlePay': {
        // Digital wallets don't need details upfront
        // The pipeline steps will handle requesting/validating them
        return {
          ...baseContext,
          paymentMethod,
        } as PipelineStepContext;
      }
      case 'giftCard': {
        const accounts = userAccountsData.userAccounts?.accounts ?? [];
        const giftCardAccount = selectedGiftCardAccountId
          ? accounts.find(
              (acc: any) =>
                acc?.accountIdentifier === selectedGiftCardAccountId ||
                acc?.fdAccountId === selectedGiftCardAccountId
            )
          : accounts.find((account: any) => account?.giftCard);

        if (!giftCardAccount?.giftCard) {
          return null;
        }

        const accountId =
          giftCardAccount.accountIdentifier ?? (giftCardAccount as any).fdAccountId ?? '';

        const giftCardData: GiftCardAccountData = {
          accountIdentifier: accountId,
          cardNumber: giftCardAccount.giftCard?.last4,
          currentBalance: giftCardBalance,
        };

        if (!giftCardData.accountIdentifier) {
          return null;
        }

        return {
          ...baseContext,
          paymentMethod: 'giftCard',
          giftCardAccountId: giftCardData.accountIdentifier,
          giftCardNumber: giftCardData.cardNumber ?? '',
          giftCardBalance: giftCardData.currentBalance,
        } as GiftCardContext;
      }
      // new card flow when the paymentMethod is null
      default: {
        // Return null if card data is missing
        if (!newCardData) {
          return null;
        }

        // Create & stash the encryption service for commitOrderStep
        const encryptionService = new EncryptionService();
        setEncryptionService(encryptionService);

        return {
          ...baseContext,
          paymentMethod: 'newCard',
          cardData: newCardData,
        };
      }
    }
  }, [
    serverOrder?.rbiOrderId,
    serverOrder?.cart?.storeDetails?.storeNumber,
    serverOrder?.cart?.serviceMode,
    serverOrder?.cart?.cartEntries,
    serverOrder?.cart?.subTotalCents,
    serverOrder?.cart?.totalCents,
    serverOrder?.cart?.unavailableCartEntries?.length,
    serverOrder?.status,
    orderFireOrderIn,
    getCurrentFireOrderIn,
    user?.details,
    processor,
    creditType,
    delivery,
    userCognitoId,
    maxCartLimit,
    minDeliveryAmount,
    store,
    logPurchase,
    currencyCode,
    appleConfig,
    googleConfig,
    updateUserInfo,
    paymentMethod,
    savedCardData,
    userAccountsData.userAccounts?.accounts,
    selectedGiftCardAccountId,
    newCardData,
    setEncryptionService,
    giftCardBalance,
    refetchGiftCardAccount,
  ]);

  return { buildPipelineContext };
}

function calculateTotalDeliveryOrderFeesCents(
  baseDeliveryFeeCents: number,
  deliverySurchargeFeeCents: number,
  deliveryServiceFeeCents: number,
  deliverySmallCartFeeCents: number,
  deliveryGeographicalFeeCents: number,
  deliveryFeeDiscountCents: number
) {
  const totalDeliveryOrderFeesCents =
    baseDeliveryFeeCents +
    deliverySurchargeFeeCents +
    deliveryServiceFeeCents +
    deliverySmallCartFeeCents +
    deliveryGeographicalFeeCents -
    deliveryFeeDiscountCents;
  return totalDeliveryOrderFeesCents;
}

function calculateDeliveryFeeCents(
  deliveryFeeCents: number,
  deliveryFeeDiscountCents: number,
  deliveryGeographicalFeeCents: number,
  deliveryServiceFeeCents: number,
  deliverySmallCartFeeCents: number
) {
  const totalDeliveryFeeCents =
    deliveryFeeCents -
    deliveryFeeDiscountCents -
    deliveryGeographicalFeeCents -
    deliveryServiceFeeCents -
    deliverySmallCartFeeCents;

  return totalDeliveryFeeCents;
}
